import * as React from "react";
import Uppy from "@uppy/core";
import Tus from "@uppy/tus";
import Dashboard from "@uppy/dashboard";
import Spanish from "@uppy/locales/lib/es_ES";
import "@uppy/core/dist/style.min.css";
import "@uppy/dashboard/dist/style.min.css";
import '../App.css';

const { useEffect, useState } = React;

// Add the generateUniqueFileName function
const generateUniqueFileName = (originalName: string): string => {
  const timestamp = Date.now();
  const extension = originalName.includes('.') 
    ? originalName.split('.').pop() 
    : '';
  const baseName = originalName.includes('.') 
    ? originalName.substring(0, originalName.lastIndexOf('.')) 
    : originalName;
  
  // Clean the base name to remove special characters and spaces
  const cleanBaseName = baseName
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '_');
    
  return `${cleanBaseName}_${timestamp}${extension ? `.${extension}` : ''}`;
};

const useUppyWithSupabase = () => {
  const [uppy] = useState(() => 
    new Uppy({
      restrictions: {
        maxFileSize: 20 * 1024 * 1024 * 1024, // 20GB in bytes
        maxNumberOfFiles: 1,
        allowedFileTypes: [
          'video/*'
        ]
      },
      autoProceed: true,
      locale: Spanish
    })
  );

  useEffect(() => {
    const initializeUppy = async () => {
      const params = new URLSearchParams(window.location.search);
      const authToken = params.get('token');
      const returnUrl = params.get('returnUrl');
      const bucketName = params.get('bucketName') || 'contrataciones_creaciones';

      if (!authToken) {
        console.error('No authentication token provided');
        return;
      }

      uppy.use(Tus, {
        endpoint: `${process.env.REACT_APP_SUPABASE_URL}/storage/v1/upload/resumable`,
        retryDelays: [0, 3000, 5000, 10000, 20000],
        headers: {
          authorization: `Bearer ${authToken}`,
          apikey: process.env.REACT_APP_SUPABASE_ANON_KEY || '',
        },
        uploadDataDuringCreation: true,
        removeFingerprintOnSuccess: true,
        chunkSize: 6 * 1024 * 1024,
        allowedMetaFields: [
          "bucketName",
          "objectName",
          "contentType",
          "cacheControl",
        ],
        onError: (error: Error) => console.error("Upload error:", error),
      });

      // Clear previous files when new file is added
      uppy.on('file-added', (file: any) => {
        const files = uppy.getFiles();
        if (files.length > 1) {
          uppy.removeFile(files[0].id);
        }
        
        // Generate unique filename
        const uniqueFileName = generateUniqueFileName(file.name);
        
        // Store original name if needed
        file.meta.originalName = file.name;
        
        // Update the file name
        file.name = uniqueFileName;
        
        file.meta = {
          ...file.meta,
          bucketName,
          objectName: uniqueFileName,
          contentType: file.type,
        };
      });

      // Handle successful uploads with modified return URL
      uppy.on('upload-success', (file: any, response: any) => {
        console.log('Upload success - File:', file);
        console.log('Upload success - Response:', response);
        if (returnUrl) {
          // Construct the public URL for the uploaded file
          const publicUrl = `${process.env.REACT_APP_SUPABASE_URL}/storage/v1/object/public/${bucketName}/${file.name}`;
          
          // Create the return URL with the videourl parameter
          const baseReturnUrl = decodeURIComponent(returnUrl);
          const separator = baseReturnUrl.includes('?') ? '&' : '?';
          const finalReturnUrl = `${baseReturnUrl}${separator}videourl=${encodeURIComponent(publicUrl)}`;
          
          console.log('Redirecting to:', finalReturnUrl);
          
          // Redirect to the return URL
          window.location.href = finalReturnUrl;
        }
      });
    };

    initializeUppy();

    return () => (uppy as any).close();
  }, [uppy]);

  return uppy;
};

const FileUploader = () => {
  const [error, setError] = useState('');
  const uppy = useUppyWithSupabase();

  useEffect(() => {
    uppy.use(Dashboard, {
      inline: true,
      target: "#drag-drop-area",
      height: 450,
      width: '100%',
      showProgressDetails: true,
      proudlyDisplayPoweredByUppy: false,
      note: 'Sólo videos, hasta 20gb.',
      showRemoveButtonAfterComplete: false,
      hideCancelButton: true,
      doneButtonHandler: null,
    });
  }, [uppy]);

  return (
    <div className="file-uploader-container">
      <div className="file-uploader-box">
        <div className="header-mini">
            <h1 className="upload-title">Sube tu contenido</h1>
            <img src="/Logo_Monomo-02.png" alt="Logo" className="logo" />
        </div>
        <div id="drag-drop-area" />
        {error && (
          <p className="error-message">{error}</p>
        )}
      </div>
    </div>
  );
};

export default FileUploader;